<template>
    <div class="delivery-config-sftp">
        <b-form-group label="Configuration Name" label-for="sftp-name">
            <b-form-input id="sftp-name" v-model="localConfig.name" placeholder="e.g., Production SFTP Server"
                required />
        </b-form-group>

        <b-row>
            <b-col cols="8">
                <b-form-group label="Host" label-for="sftp-host">
                    <b-form-input id="sftp-host" v-model="localConfig.host" placeholder="sftp.example.com" required />
                </b-form-group>
            </b-col>
            <b-col cols="4">
                <b-form-group label="Port" label-for="sftp-port">
                    <b-form-input id="sftp-port" type="number" v-model.number="localConfig.port" :min="1" :max="65535"
                        required />
                </b-form-group>
            </b-col>
        </b-row>

        <b-form-group label="Authentication Type" label-for="sftp-auth-type">
            <b-form-select id="sftp-auth-type" v-model="localConfig.authType" :options="authTypes" />
        </b-form-group>

        <b-form-group label="Username" label-for="sftp-username">
            <b-form-input id="sftp-username" v-model="localConfig.username" required />
        </b-form-group>

        <div v-if="localConfig.authType === 'Password'">
            <b-form-group label="Password" label-for="sftp-password">
                <b-form-input id="sftp-password" type="password" v-model="localConfig.password"
                    :placeholder="isEditing ? '(unchanged)' : 'Enter password'" />
            </b-form-group>
        </div>

        <div v-if="localConfig.authType === 'PrivateKey'">
            <b-form-group label="Private Key Path" label-for="sftp-private-key">
                <b-form-input id="sftp-private-key" v-model="localConfig.privateKeyPath"
                    placeholder="/path/to/private/key" />
            </b-form-group>

            <b-form-group label="Private Key Passphrase (optional)" label-for="sftp-passphrase">
                <b-form-input id="sftp-passphrase" type="password" v-model="localConfig.privateKeyPassphrase"
                    :placeholder="isEditing ? '(unchanged)' : 'Enter passphrase if required'" />
            </b-form-group>
        </div>

        <div class="d-flex justify-content-end mt-3">
            <b-button variant="outline-primary" size="sm" @click="testConnection" :disabled="testing">
                <b-spinner v-if="testing" small class="mr-1" />
                {{ testing ? 'Testing...' : 'Test Connection' }}
            </b-button>
        </div>

        <b-alert v-if="testResult" :variant="testResult.success ? 'success' : 'danger'" show class="mt-2 mb-0">
            {{ testResult.message }}
        </b-alert>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Watch, Mixins } from "vue-property-decorator";
import { SftpConfigRequest, reportExportService } from "@/services/reportExportService";
import ConnectionTestMixin from "@/mixins/ConnectionTestMixin";

@Component
export default class DeliveryConfigSftp extends Mixins(ConnectionTestMixin) {
    @Prop({ default: () => ({}) }) value!: SftpConfigRequest;
    @Prop({ default: false }) isEditing!: boolean;

    private localConfig: SftpConfigRequest = {
        name: "",
        host: "",
        port: 22,
        username: "",
        password: "",
        authType: "Password",
        privateKeyPath: "",
        privateKeyPassphrase: ""
    };

    private isInitializing = false;

    private authTypes = [
        { value: "Password", text: "Password" },
        { value: "PrivateKey", text: "Private Key" }
    ];

    mounted() {
        this.initializeFromValue();
    }

    @Watch("value", { deep: true })
    onValueChange() {
        if (!this.isInitializing) {
            this.initializeFromValue();
        }
    }

    @Watch("localConfig", { deep: true })
    onLocalConfigChange() {
        if (!this.isInitializing) {
            this.$emit("input", { ...this.localConfig });
        }
    }

    private initializeFromValue() {
        if (this.value) {
            this.isInitializing = true;
            this.localConfig = {
                id: this.value.id,
                name: this.value.name || "",
                host: this.value.host || "",
                port: this.value.port || 22,
                username: this.value.username || "",
                password: this.value.password || "",
                authType: this.value.authType || "Password",
                privateKeyPath: this.value.privateKeyPath || "",
                privateKeyPassphrase: this.value.privateKeyPassphrase || ""
            };
            this.$nextTick(() => {
                this.isInitializing = false;
            });
        }
    }

    private async testConnection() {
        await this.performConnectionTest(() =>
            reportExportService.testSftpConnection(this.localConfig));
    }
}
</script>

<style scoped>
.delivery-config-sftp {
    padding: 1rem;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    background-color: #f8f9fa;
}
</style>
